<template>
  <RemotePaginatedList :hide-search="hideSearch"
                       :filter-options="filterOptions"
                       :headers="headers"
                       multi-sort
                       :show-select="showSelect"
                       v-model="selectedItemIds"
                       data-url="aafb/admin/invoice/"
                       data-key="invoices"
                       :refresh-trigger.sync="innerRefreshTrigger"
                       v-bind="$attrs">
    <template #item.invoice_no="{item}">
      {{ `PO${item.purchase_order.id.toString().padStart(8, "0")}/${item.invoice_no}` }}
    </template>
    <template #item.purchase_order.id="{value}">
      {{ `PO${value.toString().padStart(10, "0")}` }}
    </template>
    <template #item.purchase_order.recipient="{value}">
      {{ value ? $t('name', {given_name: value.given_name, surname: value.surname}) : '' }}
    </template>
    <template #item.issued_at="{value}">
      {{ $parseDate(value, 'yyyy-MM-dd') }}
    </template>
    <template #item.due_at="{value}">
      {{ $parseDate(value, 'yyyy-MM-dd') }}
    </template>
    <template #item.amount="{value}">
      {{ parseInt(value) / 100.0 }}
    </template>
    <template #item.status="{value}">
      {{ $t(`invoice_status.${value}`) }}
    </template>
    <template #item.payment_proof="{value}">
      <a v-if="value"
         :href="`${HOST}static/${value}`"
         target="_blank">
        <v-btn icon>
          <v-icon color="green">mdi-check-circle-outline</v-icon>
        </v-btn>
      </a>
    </template>
    <template #item.payment_method="{value}">
      {{ value ? $t(`payment_method.${value}`) : '' }}
    </template>
    <template v-slot:item.actions="{item}">
      <v-tooltip top>
        <template #activator="{on, attrs}">
          <v-btn icon
                 v-on="on"
                 v-bind="attrs"
                 @click="editItem(item)">
            <v-icon>fas fa-search</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('detail') }}</span>
      </v-tooltip>

      <v-tooltip top>
        <template #activator="{on, attrs}">
          <v-btn icon
                 v-on="on"
                 v-bind="attrs"
                 v-if="item.status !== 'SETTLED'"
                 @click="deleteItem(item)">
            <v-icon>fas fa-trash</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('delete') }}</span>
      </v-tooltip>
    </template>
  </RemotePaginatedList>
</template>

<script>
import _ from 'lodash';
import {computed, onMounted, ref, watch} from '@vue/composition-api'
import RemotePaginatedList from "@/components/RemotePaginatedList";
import {BOOKING_STATUS, BOOKING_STATUS_LABEL} from "@/constants";
import EditInvoiceModal from "@/components/acc_booking/invoice/EditInvoiceModal.vue";
import {HOST} from "@/main";

export default {
  name: 'AdminInvoiceList',
  components: {RemotePaginatedList},
  props: {
    value: Array,
    hideSearch: Boolean,
    showSelect: Boolean,
    refreshTrigger: Boolean,
    extraParams: Object,
  },
  setup(props, {root, emit}) {
    const refresh = function () {
      innerRefreshTrigger.value = true;
    };
    onMounted(() => {
      refresh();
    });

    watch(() => props.refreshTrigger, (newValue) => {
      if (newValue) {
        refresh();
        emit('update:refresh-trigger', false);
      }
    })

    // Filter
    const filterOptions = computed(() => {
      return [
        {text: root.$t('admin_acc_invoice.invoice_no'), value: 'full_invoice_no', contains: true},
        {text: root.$t('admin_acc_purchase_order.recipient_hkiarace_no'), value: 'purchase_order.recipient.hkiarace_acconut.id'},
        {text: root.$t('admin_acc_invoice.issued_at'), value: 'issued_at', datetime_date_range: true},
        {text: root.$t('admin_acc_invoice.due_at'), value: 'due_at', datetime_date_range: true},
        {text: root.$t('admin_acc_invoice.title'), value: 'title', contains: true},
        {
          text: root.$t('admin_acc_invoice.status'), value: 'status',
          options: [
            {text: root.$t('invoice_status.DRAFT'), value: 'DRAFT'},
            {text: root.$t('invoice_status.AWAITING_PAYMENT'), value: 'AWAITING_PAYMENT'},
            {text: root.$t('invoice_status.PENDING_CONFIRMATION'), value: 'PENDING_CONFIRMATION'},
            {text: root.$t('invoice_status.SETTLED'), value: 'SETTLED'},
            {text: root.$t('invoice_status.VOIDED'), value: 'VOIDED'},
          ],
        },
        {
          text: root.$t('admin_acc_invoice.payment_method'), value: 'payment_method',
          options: [
            {text: root.$t('payment_method.cyber_source'), value: 'cvber_source'},
            {text: root.$t('payment_method.bank_transfer'), value: 'bank_transfer'},
            {text: root.$t('payment_method.cheque'), value: 'cheque'},
          ],
        },
      ]
    })

    // Data Table
    const headers = ref([
      {text: root.$t('admin_acc_invoice.invoice_no'), value: 'invoice_no', sortable: false},
      // {text: root.$t('admin_acc_purchase_order.id'), value: 'purchase_order.id'},
      {text: root.$t('admin_acc_purchase_order.recipient'), value: 'purchase_order.recipient', sortable: false},
      {text: root.$t('admin_acc_invoice.issued_at'), value: 'issued_at'},
      {text: root.$t('admin_acc_invoice.due_at'), value: 'due_at'},
      {text: root.$t('admin_acc_invoice.title'), value: 'title', sortable: false},
      {text: root.$t('admin_acc_invoice.amount'), value: 'amount', sortable: false},
      {text: root.$t('admin_acc_invoice.status'), value: 'status', sortable: false},
      {text: root.$t('admin_acc_invoice.payment_method'), value: 'payment_method', sortable: false},
      {text: root.$t('admin_acc_invoice.payment_proof'), value: 'payment_proof', sortable: false},
      {text: '', value: 'actions', sortable: false},
    ]);
    const innerRefreshTrigger = ref(false);

    const selectedItemIds = ref([]);
    watch(() => selectedItemIds.value, (newValue) => {
      emit('input', newValue);
    });

    watch(() => props.value, (newValue, oldValue) => {
      if (_.xor(newValue, oldValue).length) {
        selectedItemIds.value = newValue;
      }
    }, {deep: true, immediate: true})

    const deleteItem = function (item) {
      if (confirm(root.$t('admin_acc_invoice.delete_confirm_msg'))) {
        root.$callAPI('aafb/admin/invoice/delete/', {
          invoice_id: item.id,
        }, (response) => {
          refresh();
        })
      }
    }
    const editItem = async function (item) {
      const needRefresh = await root.$dialog.showAndWait(EditInvoiceModal, {
        invoiceId: item.id,
        persistent: true,
        width: '80vw',
      });

      if (needRefresh) {
        refresh();
      }
    };

    return {
      filterOptions,

      headers,
      innerRefreshTrigger,
      selectedItemIds,

      BOOKING_STATUS,
      BOOKING_STATUS_LABEL,

      deleteItem,
      editItem,

      HOST,
    }
  }
}
</script>
